import { useRef, useState } from 'react';
import { useAlert } from 'glints-aries/es/@next';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { USER_ROLES } from '../../../common/constants';
import { useCreateRoleHandoff } from '../../../common/hooks/requests/useCreateRoleHandoff';
import config from '../../../config';
import { getIsFeatureEnabled } from '../../../selectors/features';
import { getUser } from '../../../selectors/user';
import { trackModalViewedEvent } from '../../GlintsTracking/tracking';
import { UnleashFeatureNames } from '../../Unleash/featureNames';
import {
  getRoleHandoffCountryCode,
  submitCandidateRoleHandoff,
} from './submitCandidateRoleHandoff';

export default function useRoleSwitch() {
  const intl = useIntl();
  const { open: showAlert } = useAlert();
  const me = useSelector(getUser);
  const isFeatureEnabled = useSelector(state =>
    getIsFeatureEnabled(state, UnleashFeatureNames.empRoleSwitching)
  );
  const { createRoleHandoff, isCreatingRoleHandoff } = useCreateRoleHandoff();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const isSwitchingRef = useRef(false);

  const countryCode = getRoleHandoffCountryCode(config.COUNTRY);
  const isSwitching = isCreatingRoleHandoff || isRedirecting;
  const isEnabled =
    isFeatureEnabled && me?.role === USER_ROLES.COMPANY && Boolean(countryCode);
  const setRoleSwitchInProgress = (inProgress: boolean) => {
    isSwitchingRef.current = inProgress;
    setIsRedirecting(inProgress);
  };

  const openModal = () => {
    setIsModalOpen(true);
    trackModalViewedEvent('modal_role_switch_confirm');
  };

  const closeModal = () => {
    if (!isSwitching) setIsModalOpen(false);
  };

  const confirmSwitch = async () => {
    if (!countryCode || isSwitchingRef.current) return;

    setRoleSwitchInProgress(true);

    try {
      const response = await createRoleHandoff({
        targetRole: 'CANDIDATE',
        countryCode,
      });
      submitCandidateRoleHandoff(
        config.CANDIDATE_URL,
        response.code,
        countryCode
      );
    } catch (error) {
      setRoleSwitchInProgress(false);
      console.error(error);
      showAlert({
        status: 'error',
        zIndex: 1000,
        content: intl.formatMessage({
          id: 'text-switch-role-error',
          defaultMessage: 'Unable to switch roles. Please try again.',
        }),
      });
    }
  };

  return {
    closeModal,
    confirmSwitch,
    isEnabled,
    isModalOpen,
    isSwitching,
    openModal,
  } as const;
}
